import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useFetch } from "@/core/api/common";
import { useAuth } from "@/core/composables";
import { Logger, toSameOriginPath } from "@/core/utilities";
import { TabsType, useBroadcast, reloadAndOpenMainPage } from "@/shared/broadcast";
import { useNotifications } from "@/shared/notification";
import { useUser } from "./useUser";
import type { IdentityErrorType } from "@/core/api/graphql/types";
import type { ConnectTokenResponseType, TwoFactorChallengeType } from "@/core/types";

type StepType = "idle" | "verify" | "impersonate" | "success";

export function useImpersonate() {
  const {
    authorize,
    errors: authErrors,
    resetErrors: resetAuthErrors,
    twoFactorChallenge: authTwoFactorChallenge,
    setTokenType,
    setAccessToken,
    setExpiresAt,
    setRefreshToken,
  } = useAuth();
  const broadcast = useBroadcast();
  const notifications = useNotifications();
  const { t } = useI18n();

  const step = ref<StepType>("idle");
  const errors = ref<IdentityErrorType[]>([]);

  const twoFactorChallenge = ref<TwoFactorChallengeType>();

  const loading = computed<boolean>(() => step.value === "verify" || step.value === "impersonate");

  function resetState(): void {
    step.value = "idle";
    errors.value = [];
    twoFactorChallenge.value = undefined;
  }

  function failTwoFactor(error: IdentityErrorType): void {
    const impersonationErrors = errors.value;
    resetState();
    errors.value = impersonationErrors.length > 0 ? impersonationErrors : [error];
  }

  async function requestImpersonateToken(userId: string, redirectTo: string = "/"): Promise<void> {
    step.value = "impersonate";

    try {
      const { error, data } = await useFetch("/connect/token")
        .post(
          new URLSearchParams({
            grant_type: "impersonate",
            scope: "offline_access",
            user_id: userId,
          }),
          "application/x-www-form-urlencoded",
        )
        .json<ConnectTokenResponseType>();

      if (!data.value || error.value) {
        errors.value = [{ code: "impersonate_failed" }];
        step.value = "idle";
        return;
      }

      const {
        access_token,
        token_type,
        expires_in,
        refresh_token,
        error: tokenError,
        errors: tokenErrors,
      } = data.value;

      if (access_token && token_type && expires_in && refresh_token) {
        setAccessToken(access_token);
        setExpiresAt(expires_in);
        setTokenType(token_type);
        setRefreshToken(refresh_token);
        step.value = "success";

        // Ordering invariant: tokens must be persisted to localStorage (above) BEFORE the
        // broadcast fires, so other tabs read the new session on reload. Don't reorder.
        // We use OTHERS + explicit location.href so the current tab can navigate to a
        // non-"/" URL while other tabs follow the existing reload-to-home behavior.
        setTimeout(() => {
          void broadcast.emit(reloadAndOpenMainPage, null, TabsType.OTHERS);
          location.href = toSameOriginPath(redirectTo);
        }, 1000);
      } else {
        notifications.error({ text: t("pages.account.impersonate.error") });
        Logger.error(requestImpersonateToken.name, tokenError, tokenErrors);
        errors.value = [{ code: "impersonate_failed" }];
        step.value = "idle";
      }
    } catch (e) {
      notifications.error({ text: t("pages.account.impersonate.error") });
      Logger.error(requestImpersonateToken.name, e);
      errors.value = [{ code: "impersonate_failed" }];
      step.value = "idle";
    }
  }

  async function revertImpersonate(redirectTo: string = "/"): Promise<void> {
    resetState();
    await requestImpersonateToken("", redirectTo);
  }

  async function impersonate(supportEmail: string, supportPassword: string, targetUserId: string): Promise<void> {
    resetState();

    step.value = "verify";

    let authThrew = false;
    try {
      await authorize(supportEmail, supportPassword);
    } catch (e) {
      authThrew = true;
      Logger.error(impersonate.name, e);
    }

    const verifyErrors = authErrors.value;
    const hasAuthErrors = !!verifyErrors && verifyErrors.length > 0;

    // Non-400 failures (network, 5xx, etc.) leave authErrors empty but throw the promise.
    // If authorize threw without producing an errors array, the token was NOT written —
    // proceeding to requestImpersonateToken would emit unauthorizedErrorEvent and redirect
    // to /sign-in. Surface a generic error to the form instead.
    if (authThrew && !hasAuthErrors) {
      errors.value = [{ code: "generic" }];
      step.value = "idle";
      return;
    }

    twoFactorChallenge.value = authTwoFactorChallenge.value;
    if (twoFactorChallenge.value) {
      resetAuthErrors();
      step.value = "idle";
      return;
    }

    if (hasAuthErrors) {
      errors.value = verifyErrors;
      step.value = "idle";
      return;
    }

    await requestImpersonateToken(targetUserId);
  }

  async function impersonateAuthenticated(targetUserId: string): Promise<void> {
    errors.value = [];
    await requestImpersonateToken(targetUserId);
  }

  const { operator } = useUser();
  const reverting = ref(false);

  const backToOperatorLabel = computed(() =>
    operator.value
      ? t("shared.layout.header.top_header.back_to_operator", {
          name: operator.value.contact?.fullName || operator.value.userName,
        })
      : "",
  );

  async function backToOperator(): Promise<void> {
    reverting.value = true;
    try {
      await revertImpersonate("/company/members");
    } catch (e) {
      Logger.error(backToOperator.name, e);
    }
    // requestImpersonateToken handles its own errors and never re-throws.
    // On success step="success" and the page reloads shortly after, so the loader
    // stays visible until then. On any failure step is reset to "idle" — release
    // the loader so the user can retry.
    if (step.value !== "success") {
      reverting.value = false;
    }
  }

  return {
    impersonate,
    impersonateAuthenticated,
    revertImpersonate,
    backToOperator,
    backToOperatorLabel,
    reverting,
    loading,
    step,
    errors,
    twoFactorChallenge,
    resetState,
    failTwoFactor,
  };
}
